import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    IconButton,
    Breadcrumbs,
    Link,
    CircularProgress,
    Tooltip,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import {
    Folder as FolderIcon,
    InsertDriveFile as FileIcon,
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Image as ImageIcon,
    PictureAsPdf as PdfIcon,
    VideoFile as VideoIcon,
    AudioFile as AudioIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const FileExplorer = ({ fetchFiles, deleteFolder, initialPath = 'root', showAllFiles = false }) => {
    const [files, setFiles] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPath, setCurrentPath] = useState(initialPath);
    const [pathHistory, setPathHistory] = useState([{ id: initialPath, name: 'Ana Klasör' }]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Dosya türüne göre simge seç
    const getFileIcon = (mimeType) => {
        if (mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
        if (mimeType === 'application/pdf') return <PdfIcon color="error" />;
        if (mimeType.startsWith('video/')) return <VideoIcon color="secondary" />;
        if (mimeType.startsWith('audio/')) return <AudioIcon color="success" />;
        return <FileIcon color="action" />;
    };

    // Dosya boyutunu formatla
    const formatFileSize = (bytes) => {
        if (!bytes) return 'Bilinmiyor';
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes === 0) return '0 Byte';
        const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
        return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
    };

    // Dosya ve klasörleri getir
    const loadFilesAndFolders = async () => {
        setLoading(true);
        try {
            const data = await fetchFiles(currentPath, showAllFiles);

            // Klasörler ve dosyaları ayır
            const folderList = data.filter(item => item.mimeType === 'application/vnd.google-apps.folder');
            const fileList = data.filter(item => item.mimeType !== 'application/vnd.google-apps.folder');

            setFolders(folderList);
            setFiles(fileList);
        } catch (error) {
            toast.error('Dosyalar yüklenirken bir hata oluştu');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Klasöre gir
    const navigateToFolder = (folderId, folderName) => {
        setCurrentPath(folderId);
        setPathHistory([...pathHistory, { id: folderId, name: folderName }]);
    };

    // Geri git
    const navigateBack = () => {
        if (pathHistory.length > 1) {
            const newHistory = [...pathHistory];
            newHistory.pop(); // Son klasörü kaldır
            const previousFolder = newHistory[newHistory.length - 1];
            setCurrentPath(previousFolder.id);
            setPathHistory(newHistory);
        }
    };

    // Belirli bir klasöre git (breadcrumb'dan)
    const navigateToBreadcrumb = (index) => {
        if (index < pathHistory.length) {
            const newHistory = pathHistory.slice(0, index + 1);
            setCurrentPath(newHistory[index].id);
            setPathHistory(newHistory);
        }
    };

    // Yenile
    const refreshFiles = () => {
        loadFilesAndFolders();
    };

    // Menü aç
    const handleMenuOpen = (event, item) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    // Menü kapat
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        // setSelectedItem(null); // Bu satırı kaldırın
    };

    // Klasör silme dialogunu aç
    // Klasör silme dialogunu aç
    const handleDeleteDialogOpen = () => {
        // Önce selectedItem'ı bir değişkene kaydet
        const itemToDelete = selectedItem;
        
        // Sonra dialog'u aç ve menüyü kapat
        setDeleteDialogOpen(true);
        handleMenuClose();
        
        // Son olarak selectedItem'ı tekrar ayarla
        setSelectedItem(itemToDelete);
    };

    // Klasör silme dialogunu kapat
    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
    };

    // Klasörü sil
    const handleDeleteFolder = async () => {
        if (!selectedItem) {
            console.error('selectedItem bulunamadı');
            return;
        }
        
        console.log('Silinecek klasör:', selectedItem);
        
        try {
            await deleteFolder(selectedItem.id);
            toast.success(`${selectedItem.name} klasörü silindi`);
            refreshFiles();
        } catch (error) {
            console.error('Silme hatası detayı:', error);
            toast.error(error.message || 'Klasör silinirken bir hata oluştu');
        } finally {
            handleDeleteDialogClose();
        }
    };

    // İlk yükleme ve klasör değişikliklerinde dosyaları getir
    useEffect(() => {
        loadFilesAndFolders();
    }, [currentPath, showAllFiles]);

    return (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
            {/* Üst Kontrol Çubuğu */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton
                    onClick={navigateBack}
                    disabled={pathHistory.length <= 1}
                    sx={{ mr: 1 }}
                >
                    <ArrowBackIcon />
                </IconButton>

                <Breadcrumbs sx={{ flexGrow: 1 }}>
                    {pathHistory.map((path, index) => (
                        <Link
                            key={path.id}
                            component="button"
                            variant="body1"
                            onClick={() => navigateToBreadcrumb(index)}
                            color={index === pathHistory.length - 1 ? 'text.primary' : 'inherit'}
                            sx={{ textDecoration: 'none', cursor: 'pointer' }}
                        >
                            {path.name}
                        </Link>
                    ))}
                </Breadcrumbs>

                <Tooltip title="Yenile">
                    <IconButton onClick={refreshFiles}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Dosya ve Klasör Görünümü */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {/* Klasörler */}
                    {folders.map((folder) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={folder.id}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    position: 'relative',
                                    height: '100%',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                                onClick={() => navigateToFolder(folder.id, folder.name)}
                            >
                                {/* Klasör için menü butonu */}
                                <IconButton 
                                  size="small" 
                                  sx={{ position: 'absolute', top: 0, right: 0 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMenuOpen(e, folder);
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                                {folder.name.toLowerCase().includes('event') && (
                                    <IconButton 
                                        size="small" 
                                        sx={{ position: 'absolute', top: 0, right: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMenuOpen(e, folder);
                                        }}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                )}

                                <FolderIcon sx={{ fontSize: 60, color: 'primary.main', mb: 1 }} />
                                <Typography
                                    variant="body2"
                                    align="center"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {folder.name}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}

                    {/* Dosyalar */}
                    {files.map((file) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={file.id}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    height: '100%',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                                onClick={() => window.open(file.webViewLink, '_blank')}
                            >
                                {file.thumbnailLink ? (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 120,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            mb: 1,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <img
                                            src={file.thumbnailLink}
                                            alt={file.name}
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 120,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            mb: 1,
                                            bgcolor: 'grey.100'
                                        }}
                                    >
                                        {getFileIcon(file.mimeType)}
                                    </Box>
                                )}
                                <Typography
                                    variant="body2"
                                    align="center"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {file.name}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}

                    {folders.length === 0 && files.length === 0 && (
                        <Grid item xs={12}>
                            <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                                Bu klasörde hiçbir dosya veya klasör bulunamadı.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Klasör Menüsü */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleDeleteDialogOpen}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    Klasörü Sil
                </MenuItem>
            </Menu>

            {/* Klasör Silme Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
            >
                <DialogTitle>Klasörü Sil</DialogTitle>
                <DialogContent>
                    {selectedItem ? (
                        <Typography variant="body1">
                            {selectedItem.name} klasörünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
                        </Typography>
                    ) : (
                        <Typography variant="body1" color="error">
                            Silinecek klasör bilgisi bulunamadı. Lütfen tekrar deneyin.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose}>İptal</Button>
                    <Button 
                        onClick={handleDeleteFolder} 
                        color="error" 
                        variant="contained"
                        disabled={!selectedItem}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default FileExplorer;